import axios from 'axios';

import { APIResponse, BaseAPIService } from './base.api.service';
import { TCreateCommentRequest, TDeleteCommentRequest, TDeleteReplyToCommentRequest, TEditCommentRequest, TEditReplyToCommentRequest, TGetCommentsRequest, TReplyToCommentRequest } from './apiEndpoint.types.ts';
import { DemoDayBooking, Payment, RazorpayOptions, VerifyPayment } from 'src/store/venture/venture.types';
import { TCofounderRequest, TSingleVentureRequest, TVenture, TVentureCounter, TVentureRequest } from './apiEndpoint.types.ts/ventures/ventures.types';

export class VentureAPIService extends BaseAPIService {
    private createventurepath: string = '/venture-verse/create';
    private updateventurepath: string = '/venture-verse/update';
    private searchcofounderpath: string = '/venture-verse/cofounders/search';
    private uploadFilePath: string = '/venture-verse/uploads';
    private ventureList: string = '/venture-verse/list';
    private singleVenturePath: string = '/venture-verse/get';
    private counterPath: string = '/venture-verse/counters';
    private likepath: string = '/venture-verse/like';
    private unlikepath: string = '/venture-verse/unlike';
    private getCommentsPath: string = '/venture-verse/comments/list';
    private createCommentPath: string = '/venture-verse/comment/create';
    private editCommentPath: string = '/venture-verse/comment/update';
    private getRepliesPath: string = '/venture-verse/replies/list';
    private replyToCommentPath: string = '/venture-verse/reply/create';
    private deleteCommentPath: string = '/venture-verse/comment/delete';
    private editReplyToCommentPath: string = '/venture-verse/replies/update';
    private deleteReplyToCommentPath: string = '/venture-verse/replies/delete';
    private getDemoDayPath: string = '/venture-verse/demo-day-details';
    private demodayparticipatepath: string = '/venture-verse/participate/demo-day';
    private paymentPath: string = '/venture-verse/demo-day/create-order';
    private verifypaymentPath: string = '/venture-verse/demo-day/verify-order';
    private paymentAddPath: string = '/venture-verse/demo-day/create-booking';
    private ongoingdemodaypath: string = '/venture-verse/get-ongoing-demo-day';

    constructor() {
        super();
        this.axiosInstance = axios.create({ baseURL: process.env.REACT_APP_API_IDEAHUB_ENDPOINT });
        this.configureInterceptors();
    }

    public async getCoFounder(
        cofoundersearchRequset: TCofounderRequest
    ) {
        const coFounderSearchResponse = await this.get(
            this.searchcofounderpath,
            true,
            cofoundersearchRequset
        );
        return coFounderSearchResponse;
    }

    public async createVenture(
        createVentureRequest: TVenture
    ) {
        const createVentureResponse = await this.post(
            this.createventurepath,
            createVentureRequest,
            true
        );
        return createVentureResponse;
    }

    public async getComments<TGetCommentsResponse>(getCommentsRequest: TGetCommentsRequest) {
        const getCommentsResponse: APIResponse<TGetCommentsResponse> = await this.get(
            this.getCommentsPath,
            true,
            getCommentsRequest
        );
        return getCommentsResponse;
    }

    public async createComment<TCreateCommentResponse>(createCommentRequest: TCreateCommentRequest) {
        const createCommentResponse: APIResponse<TCreateCommentResponse> = await this.post(
            this.createCommentPath,
            createCommentRequest,
            true
        );
        return createCommentResponse;
    }

    public async editComment<TEditCommentResponse>(editCommentRequest: TEditCommentRequest) {
        const editCommentResponse: APIResponse<TEditCommentResponse> = await this.patch(
            this.editCommentPath,
            editCommentRequest,
            true
        );
        return editCommentResponse;
    }

    public async deleteComment(deleteCommentRequest) {
        const deleteCommentResponse = await this.delete(
            this.deleteCommentPath,
            true,
            deleteCommentRequest
        );
        return deleteCommentResponse;
    }

    public async likeVentureVerse(
        createVentureLikeRequest
    ) {
        const createVentureLikeResponse = await this.post(
            this.likepath,
            createVentureLikeRequest,
            true
        );
        return createVentureLikeResponse;
    }

    public async unlikeVentureVerse(
        createVentureUnLikeRequest
    ) {
        const createVentureUnLikeResponse = await this.delete(
            this.unlikepath,
            true,
            createVentureUnLikeRequest
        );
        return createVentureUnLikeResponse;
    }

    public async updateVenture(
        updateVentureRequest: TVenture
    ) {
        const updateVentureResponse = await this.patch(
            this.updateventurepath,
            updateVentureRequest,
            true
        );
        return updateVentureResponse;
    }

    public async getPresignedUrl(
        presignedRequest
    ) {
        const presignedResponse =
            await this.get(
                this.uploadFilePath,
                true,
                presignedRequest
            );
        return presignedResponse;
    }

    public async getVentureList(
        ventureRequest: TVentureRequest | TSingleVentureRequest
    ) {
        const ventureResponse =
            await this.get(
                this.ventureList,
                false,
                ventureRequest
            );
        return ventureResponse;
    }

    public async getSingleVenture(
        ventureRequest: TSingleVentureRequest
    ) {
        const ventureResponse =
            await this.get(
                this.singleVenturePath,
                false,
                ventureRequest
            );
        return ventureResponse;
    }

    public async getVentureCounter(
        repliesRequest: TSingleVentureRequest
    ) {
        const repliesResponse: { data: TVentureCounter, status: number } =
            await this.get(
                this.counterPath,
                false,
                repliesRequest
            );
        return repliesResponse;
    }

    public async getReplies(
        repliesRequest
    ) {
        const counterResponse =
            await this.get(
                this.getRepliesPath,
                // false,
                true,
                repliesRequest
            );
        return counterResponse;
    }

    public async replyToComment<TReplyToCommentResponse>(
        replyToCommentRequest: TReplyToCommentRequest
    ) {
        const replyToCommentResponse: APIResponse<TReplyToCommentResponse> = await this.post(
            this.replyToCommentPath,
            replyToCommentRequest,
            true
        );
        return replyToCommentResponse;
    }

    public async editReplyToComment<TEditReplyToCommentResponse>(
        editReplyToCommentRequest: TEditReplyToCommentRequest
    ) {
        const editReplyToCommentResponse: APIResponse<TEditReplyToCommentResponse> = await this.patch(
            this.editReplyToCommentPath,
            editReplyToCommentRequest,
            true
        );
        return editReplyToCommentResponse;
    }

    public async deleteReplyToComment<TDeleteReplyToCommentResponse>(
        deleteReplyToCommentRequest: TDeleteReplyToCommentRequest
    ) {
        const deleteReplyToCommentResponse: APIResponse<TDeleteReplyToCommentResponse> =
            await this.delete(this.deleteReplyToCommentPath, true, deleteReplyToCommentRequest);
        return deleteReplyToCommentResponse;
    }

    public async getDemoDay(
        demoDayRequest: { ventureVerseId: string }
    ) {
        const demoDayResponse =
            await this.get(
                this.getDemoDayPath,
                true,
                null,
                false,
                demoDayRequest
            );
        return demoDayResponse;
    }

    public async ongoingdemoday(
        getOngoingDemoDay
    ) {
        const demoDayResponse =
            await this.get(
                this.ongoingdemodaypath,
                true,
                null,
                false,
                getOngoingDemoDay
            );
        return demoDayResponse;
    }

    public async payment(
        paymentRequest: Payment
    ) {
        const paymentResponse = await this.post(
            this.paymentPath,
            paymentRequest,
            true
        );
        return paymentResponse;
    }

    public async registerDemoDay(
        registerDemoDayRequest: DemoDayBooking
    ) {
        const registerDemoDayResponse = await this.post(
            this.demodayparticipatepath,
            registerDemoDayRequest,
            true
        );
        return registerDemoDayResponse;
    }

    public async verifyOrder(
        verifyPaymentRequest: VerifyPayment
    ) {
        const verifyPaymentResponse = await this.post(
            this.verifypaymentPath,
            verifyPaymentRequest,
            true
        );
        return verifyPaymentResponse;
    }

    public async paymentAdd(paymentAddRequest) {
        const paymentAddResponse = await this.patch(
            this.paymentAddPath,
            paymentAddRequest,
            true
        );
        return paymentAddResponse;
    }
}
